
import React, { useState } from 'react';
import { ChevronsLeft, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Shop } from '@/lib/supabase/shops/types';
import { SearchInput } from './expanded-filter/SearchInput';
import { ShopsList } from './expanded-filter/ShopsList';
import AddShopDialog from './expanded-filter/AddShopDialog';
import { useNavigate } from 'react-router-dom';

interface ExpandedShopFilterProps {
  setIsCollapsed: (collapsed: boolean) => void;
  shops: Shop[];
  isLoading: boolean;
  toggleShopSelection: (id: string) => void;
  isSingleSelectionMode: boolean;
  singleSelectedShop: string;
  selectedShops: string[];
  showFilterButton?: boolean;
  onSettingsClick?: () => void;
  refreshShops?: () => Promise<void>;
  searchTerm?: string;
  setSearchTerm?: (term: string) => void;
  title?: string;
  filterButtonLabel?: string;
  handleAddShop?: () => void;
}

const ExpandedShopFilter: React.FC<ExpandedShopFilterProps> = ({
  setIsCollapsed,
  shops,
  isLoading,
  toggleShopSelection,
  isSingleSelectionMode,
  singleSelectedShop,
  selectedShops,
  showFilterButton = true,
  refreshShops,
  searchTerm = '',
  setSearchTerm,
  title = 'Shops',
  filterButtonLabel = 'Apply filter',
  handleAddShop
}) => {
  const [isAddShopDialogOpen, setIsAddShopDialogOpen] = useState(false);
  const navigate = useNavigate();
  
  // Filter shops based on search term
  const filteredShops = shops.filter(shop => 
    shop.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const handleShopSettingsClick = (shopId: string) => {
    navigate('/dashboard/shop-settings', { 
      state: { 
        shopId: shopId,
        activeMenu: 'profile'
      } 
    });
  };

  return (
    <div className="expanded-shops-sidebar h-full w-64 flex flex-col border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
      <div className="px-6 py-4 flex items-center justify-between flex-shrink-0 border-b border-gray-200 dark:border-gray-800">
        <h2 className="shops-title text-base font-medium text-gray-800 dark:text-gray-200">{title}</h2>
        <div className="flex items-center">
          <button 
            onClick={handleAddShop || (() => setIsAddShopDialogOpen(true))} 
            className="text-custom-primary hover:text-custom-dark dark:text-custom-secondaryLight dark:hover:text-custom-secondaryLight/80 mr-3"
            aria-label="Add shop"
          >
            <Plus className="h-5 w-5" />
          </button>
          
          <button 
            onClick={() => setIsCollapsed(true)} 
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            aria-label="Collapse sidebar"
          >
            <ChevronsLeft className="h-5 w-5" />
          </button>
        </div>
      </div>
      
      {setSearchTerm && (
        <SearchInput 
          searchTerm={searchTerm} 
          onChange={(e) => setSearchTerm(e.target.value)} 
        />
      )}
      
      <div className="px-4 flex-1 overflow-y-auto">
        <ShopsList 
          shops={filteredShops} 
          isLoading={isLoading} 
          toggleShopSelection={toggleShopSelection} 
          isSingleSelectionMode={isSingleSelectionMode} 
          singleSelectedShop={singleSelectedShop} 
          selectedShops={selectedShops}
          onShopSettingsClick={handleShopSettingsClick} 
        />
      </div>
      
      {showFilterButton && (
        <div className="p-4 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          <Button 
            className="w-full bg-custom-primary text-white hover:bg-custom-dark dark:bg-custom-primary dark:hover:bg-custom-secondaryLight dark:hover:text-gray-800"
          >
            {filterButtonLabel}
          </Button>
        </div>
      )}
      
      <AddShopDialog 
        isOpen={isAddShopDialogOpen} 
        setIsOpen={setIsAddShopDialogOpen} 
        refreshShops={refreshShops}
      />
    </div>
  );
};

export default ExpandedShopFilter;
